import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getSystemFeatures } from '../services/api';

/**
 * Defaults conservadores — usados como fallback enquanto carrega ou se config não acessível.
 * true = seguro, não quebra nada.
 */
const DEFAULT_FEATURES = {
    smartScore:            true,
    aiQualitativeAnalysis: true,
    shareOfVoice:          true,
    briefingAssistant:     true,
    newsletter:            true,
    publicDashboard:       true,
    powerBi:               false,
};

const SystemFeaturesContext = createContext({
    features: DEFAULT_FEATURES,
    globalFeatures: DEFAULT_FEATURES,
    packageFeatures: DEFAULT_FEATURES,
    loading: false,
    reload: () => {},
    /**
     * Retorna o estado efetivo de uma feature para uma determinada marca.
     * Regra: global é master. Se global=false → false.
     * Se global=true → usa override da marca (ou true se null).
     */
    resolveFeature: (_key, _brandFeatures) => true,
});

/**
 * Resolve o estado efetivo de uma feature aplicando a hierarquia:
 *   global (master) → brand override → default true
 */
export const resolveFeature = (globalFeatures, packageFeatures, brandFeatures, key) => {
    const globalVal = globalFeatures?.[key];

    // Global desabilitado = ninguém acessa
    if (globalVal === false) return false;

    const packageVal = packageFeatures?.[key];
    if (packageVal === false) return false;

    // Brand override
    const brandVal = brandFeatures?.[key];
    if (brandVal === false) return false;
    if (brandVal === true) return true;

    // null/undefined na brand = herda do global (que já é true aqui)
    return globalVal !== false; // true or undefined → true
};

const parseGlobalFeatures = (rawFeatures = {}) => {
    return Object.fromEntries(
        Object.entries(DEFAULT_FEATURES).map(([key, defaultVal]) => {
            const feat = rawFeatures[key];
            if (feat && typeof feat === 'object' && 'enabled' in feat) return [key, feat.enabled];
            if (typeof feat === 'boolean') return [key, feat];
            return [key, defaultVal];
        })
    );
};

const parseBooleanFeatures = (rawFeatures = {}, defaults = DEFAULT_FEATURES) => {
    return Object.fromEntries(
        Object.entries(defaults).map(([key, defaultVal]) => {
            const val = rawFeatures?.[key];
            if (typeof val === 'boolean') return [key, val];
            return [key, defaultVal];
        })
    );
};

export const SystemFeaturesProvider = ({ children }) => {
    const [globalFeatures, setGlobalFeatures] = useState(DEFAULT_FEATURES);
    const [packageFeatures, setPackageFeatures] = useState(DEFAULT_FEATURES);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getSystemFeatures();
            const data = response?.data?.data || {};
            setGlobalFeatures(parseGlobalFeatures(data.globalFeatures || {}));
            setPackageFeatures(parseBooleanFeatures(data.packageFeatures || {}, DEFAULT_FEATURES));
        } catch (err) {
            console.warn('[SystemFeatures] Usando defaults (config não acessível):', err.message);
            setGlobalFeatures(DEFAULT_FEATURES);
            setPackageFeatures(DEFAULT_FEATURES);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        window.addEventListener('active-brand-changed', load);
        return () => window.removeEventListener('active-brand-changed', load);
    }, [load]);

    const resolve = useCallback(
        (key, brandFeatures) => resolveFeature(globalFeatures, packageFeatures, brandFeatures, key),
        [globalFeatures, packageFeatures]
    );

    // `features` = features globais (compatibilidade com código existente)
    return (
        <SystemFeaturesContext.Provider value={{
            features: globalFeatures,
            globalFeatures,
            packageFeatures,
            loading,
            reload: load,
            resolveFeature: resolve,
        }}>
            {children}
        </SystemFeaturesContext.Provider>
    );
};

/**
 * Hook principal.
 * 
 * Para verificar feature considerando a marca ativa:
 *   const { resolveFeature } = useSystemFeatures();
 *   const enabled = resolveFeature('smartScore', activeBrandData?.features);
 * 
 * Para verificar apenas o global:
 *   const { features } = useSystemFeatures();
 *   if (!features.smartScore) ...
 */
export const useSystemFeatures = () => useContext(SystemFeaturesContext);

export default SystemFeaturesContext;
